import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { amountText, currencyName, parseAmount } from '../money';

export interface RangeValue {
  min: number | null;
  max: number | null;
}

export interface RangeFieldProps {
  label: string;
  value: RangeValue;
  onChange: (value: RangeValue) => void;
  currency: string;
  error?: string | undefined;
}

/** One end of a range, typed as an amount; `null` while it is not one. */
function useEnd(value: number | null, currency: string) {
  const [text, setText] = useState(() => (value === null ? '' : amountText(value, currency)));
  return { text, setText, typed: parseAmount(text, currency) };
}

/**
 * A price as one field, as a booking site's price filter: `[ 80 ] – [ 200 ] EUR`, or — its
 * Range box unticked — one amount, `[ 80 ] EUR`, a range whose lowest is its highest. Each end
 * understands what an amount field does; a reversed pair is refused here.
 */
export function RangeField({ label, value, onChange, currency, error }: RangeFieldProps) {
  const { t, i18n } = useTranslation();
  const id = useId();
  // Nothing in it yet, it is asked for as a range.
  const [ranged, setRanged] = useState(!value.min || value.min !== value.max);
  const low = useEnd(value.min, currency);
  const high = useEnd(value.max, currency);
  const bad = [low, ...(ranged ? [high] : [])].some(
    (end) => end.typed === null && end.text.trim() !== '',
  );
  const reversed = ranged && value.min !== null && value.max !== null && value.min > value.max;
  const message = bad ? t('field.amount') : reversed ? t('problem.reversed') : error;

  const end = (which: 'min' | 'max', at: typeof low) => (
    <input
      className="text-input amount-input"
      type="text"
      inputMode="decimal"
      autoComplete="off"
      // Said within its group, which is named by the label.
      aria-label={t(
        ranged ? (which === 'min' ? 'field.lowest' : 'field.highest') : 'field.price',
      )}
      value={at.text}
      aria-invalid={message !== undefined}
      onChange={(event) => {
        at.setText(event.target.value);
        const typed = parseAmount(event.target.value, currency);
        onChange(ranged ? { ...value, [which]: typed } : { min: typed, max: typed });
      }}
      onBlur={() => {
        if (at.typed !== null) at.setText(amountText(at.typed, currency));
      }}
    />
  );

  return (
    <fieldset className="range-field" aria-describedby={message === undefined ? undefined : id}>
      <legend className="field-label">{label}</legend>
      <label className="range-toggle">
        <input
          type="checkbox"
          checked={ranged}
          onChange={(event) => {
            // Back to one price: the lowest is it.
            if (!event.target.checked) onChange({ min: value.min, max: value.min });
            else high.setText(low.text);
            setRanged(event.target.checked);
          }}
        />
        {t('field.range')}
      </label>
      <div className="input-group range-group">
        {end('min', low)}
        {ranged && (
          <>
            <span className="range-dash" aria-hidden="true">
              –
            </span>
            {end('max', high)}
          </>
        )}
        <span className="input-suffix" title={currencyName(currency, i18n.language)}>
          {currency}
        </span>
      </div>
      {message !== undefined && (
        <p className="field-error" id={id}>
          {message}
        </p>
      )}
    </fieldset>
  );
}
